<template>
    <div>
        <div v-if="canViewSalesDashboard" class="sales-dashboard">
            <div class="dashboard-page-header">
                <div>
                    <p class="dashboard-eyebrow">Focused Dashboard</p>
                    <h1>Sales & Revenue</h1>
                </div>
                <router-link v-if="canViewRevenueAnalytics" class="report-link" :to="reportRoute('/reports/sale-report')">
                    Full sales report
                    <i class="material-icons" aria-hidden="true">arrow_forward</i>
                </router-link>
            </div>

            <!-- Sales Metrics Section -->
            <div v-if="canViewRevenueAnalytics" class="sales-metrics mb-4">
                <div class="row">
                    <div class="col-md-3 mb-3">
                        <div :class="['metric-card', 'p-3', 'rounded-lg', 'shadow-sm', { dark: theme === THEMES.DARK }]">
                            <div class="metric-icon">
                                <i class="material-icons text-success" aria-hidden="true">shopping_cart</i>
                            </div>
                            <div class="metric-content">
                                <h3 class="metric-value">{{ metrics.totalOrders || 0 }}</h3>
                                <p class="metric-label">Total Orders</p>
                                <small class="metric-period">{{ timeFramePeriodLabel }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div :class="['metric-card', 'p-3', 'rounded-lg', 'shadow-sm', { dark: theme === THEMES.DARK }]">
                            <div class="metric-icon">
                                <i class="material-icons text-primary" aria-hidden="true">attach_money</i>
                            </div>
                            <div class="metric-content">
                                <h3 class="metric-value">${{ formatCurrency(metrics.totalRevenue) }}</h3>
                                <p class="metric-label">Total Revenue</p>
                                <small class="metric-period">{{ timeFramePeriodLabel }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div :class="['metric-card', 'p-3', 'rounded-lg', 'shadow-sm', { dark: theme === THEMES.DARK }]">
                            <div class="metric-icon">
                                <i class="material-icons text-warning" aria-hidden="true">trending_up</i>
                            </div>
                            <div class="metric-content">
                                <h3 class="metric-value">${{ formatCurrency(metrics.avgOrderValue) }}</h3>
                                <p class="metric-label">Avg Order Value</p>
                                <small class="metric-period">{{ timeFramePeriodLabel }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div :class="['metric-card', 'p-3', 'rounded-lg', 'shadow-sm', { dark: theme === THEMES.DARK }]">
                            <div class="metric-icon">
                                <i class="material-icons text-info" aria-hidden="true">people</i>
                            </div>
                            <div class="metric-content">
                                <h3 class="metric-value">{{ metrics.uniqueCustomers || 0 }}</h3>
                                <p class="metric-label">Unique Customers</p>
                                <small class="metric-period">{{ timeFramePeriodLabel }}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sales Filters with integrated time period selector -->
            <SalesFilters 
                :search.sync="filters.search"
                :location.sync="filters.location"
                :includePilotSales.sync="filters.includePilotSales"
                :allow-historical-mode="Boolean(user && user.isAdmin)"
                :timeFrame="timeFrame"
                @change="onFiltersChange"
                @timeframe-change="onTimeFrameChange"
                @reset="resetFilters"
            />

            <section v-if="canViewAnySalesAnalytics" class="sales-analytics-grid">
                <div
                    v-if="canViewRevenueAnalytics"
                    :class="['analytics-card', 'analytics-card-wide', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Revenue Trend</h3>
                            <p>{{ analyticsDateRangeLabel }}</p>
                        </div>
                        <select v-model="trendGroupBy" class="chart-select" aria-label="Group revenue charts by interval" @change="fetchAnalyticsDashboardData">
                            <option value="day">Day</option>
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                        </select>
                    </div>
                    <div class="analytics-chart-frame large">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading revenue trend…</div>
                        <div v-else-if="analyticsErrors.revenueTrend" class="chart-state error">Revenue trend could not load.</div>
                        <EChartsLineChart
                            v-else-if="revenueTrendLabels.length"
                            :labels="revenueTrendLabels"
                            :values="revenueTrendValues"
                            series-name="Revenue"
                            aria-label="Sales dashboard revenue trend"
                            :is-dark="theme === THEMES.DARK"
                        />
                        <div v-else class="chart-state">No revenue data for this period.</div>
                    </div>
                </div>

                <div
                    v-if="canViewRevenueAnalytics"
                    :class="['analytics-card', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Tender Mix</h3>
                            <p>{{ tenderReconciliationLabel }}</p>
                        </div>
                    </div>
                    <div class="analytics-chart-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading tender mix…</div>
                        <div v-else-if="analyticsErrors.tenders" class="chart-state error">Tender mix could not load.</div>
                        <EChartsHorizontalBarChart
                            v-else-if="tenderChartCategories.length"
                            :categories="tenderChartCategories"
                            :values="tenderChartValues"
                            series-name="Net paid"
                            aria-label="Net paid amount by tender after refunds"
                            :is-dark="theme === THEMES.DARK"
                            :value-formatter="formatCurrencyDisplay"
                        />
                        <div v-else class="chart-state">No tender data for this period.</div>
                    </div>
                </div>

                <div
                    v-if="canViewStoreAnalytics"
                    :class="['analytics-card', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Sales by Store</h3>
                            <p>Revenue ranking for the selected period</p>
                        </div>
                        <router-link class="mini-link" :to="reportRoute('/reports/sale-by-location')">Report</router-link>
                    </div>
                    <div class="analytics-chart-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading stores…</div>
                        <div v-else-if="analyticsErrors.stores" class="chart-state error">Store sales could not load.</div>
                        <EChartsHorizontalBarChart
                            v-else-if="storeChartCategories.length"
                            :categories="storeChartCategories"
                            :values="storeChartValues"
                            series-name="Revenue"
                            aria-label="Sales by store revenue ranking"
                            :is-dark="theme === THEMES.DARK"
                            :value-formatter="formatCurrencyDisplay"
                        />
                        <div v-else class="chart-state">No store sales data.</div>
                    </div>
                </div>

                <div
                    v-if="canViewRevenueAnalytics"
                    :class="['analytics-card', 'analytics-card-wide', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Basket Size &amp; Items per Order</h3>
                            <p>{{ basketCoverageLabel }}</p>
                        </div>
                    </div>
                    <div v-if="analyticsLoading" class="chart-state basket-state" role="status" aria-live="polite">Loading basket behavior…</div>
                    <div v-else-if="analyticsErrors.basket" class="chart-state error basket-state">Basket behavior could not load.</div>
                    <template v-else>
                        <div class="basket-kpi-grid">
                            <article v-for="item in basketKpiCards" :key="item.title">
                                <p>{{ item.title }}</p>
                                <strong>{{ item.value }}</strong>
                            </article>
                        </div>
                        <div class="analytics-chart-frame basket-frame">
                            <EChartsDistributionChart
                                v-if="basketDistributionCategories.length"
                                :categories="basketDistributionCategories"
                                :values="basketDistributionValues"
                                series-name="Orders"
                                aria-label="Distribution of completed sales by units per order"
                                :is-dark="theme === THEMES.DARK"
                            />
                            <div v-else class="chart-state">No completed-order basket data.</div>
                        </div>
                    </template>
                </div>

                <div
                    v-if="canViewRevenueAnalytics"
                    :class="['analytics-card', 'analytics-card-wide', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Sales Activity by Weekday &amp; Hour</h3>
                            <p>{{ heatmapCoverageLabel }}</p>
                        </div>
                    </div>
                    <div class="analytics-chart-frame heatmap-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading sales activity…</div>
                        <div v-else-if="analyticsErrors.heatmap" class="chart-state error">Sales activity could not load.</div>
                        <EChartsSalesHeatmap
                            v-else-if="heatmapCells.length"
                            :cells="heatmapCells"
                            aria-label="Completed sales order count by Toronto weekday and hour"
                            :is-dark="theme === THEMES.DARK"
                        />
                        <div v-else class="chart-state">No timestamped completed sales for this period.</div>
                    </div>
                </div>

                <div
                    v-if="canViewStoreAnalytics"
                    :class="['analytics-card', 'analytics-card-wide', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Store Revenue Rank Over Time</h3>
                            <p>{{ storeRankCoverageLabel }}</p>
                        </div>
                    </div>
                    <div class="analytics-chart-frame rank-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading store rank…</div>
                        <div v-else-if="analyticsErrors.storeRank" class="chart-state error">Store rank could not load.</div>
                        <EChartsStoreRankChart
                            v-else-if="storeRankPeriods.length && storeRankChartStores.length"
                            :periods="storeRankPeriods"
                            :stores="storeRankChartStores"
                            aria-label="Store revenue rank by selected reporting period"
                            :is-dark="theme === THEMES.DARK"
                        />
                        <div v-else class="chart-state">No store ranking history for this period.</div>
                    </div>
                </div>

                <div
                    v-if="canViewProductAnalytics"
                    :class="['analytics-card', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Top Products</h3>
                            <p>Revenue concentration by product</p>
                        </div>
                        <router-link class="mini-link" :to="reportRoute('/reports/sale-by-product')">Report</router-link>
                    </div>
                    <div class="analytics-chart-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading products…</div>
                        <div v-else-if="analyticsErrors.products" class="chart-state error">Product sales could not load.</div>
                        <EChartsHorizontalBarChart
                            v-else-if="productChartCategories.length"
                            :categories="productChartCategories"
                            :values="productChartValues"
                            series-name="Revenue"
                            aria-label="Top product revenue ranking"
                            :is-dark="theme === THEMES.DARK"
                            :value-formatter="formatCurrencyDisplay"
                        />
                        <div v-else class="chart-state">No product sales data.</div>
                    </div>
                </div>

                <div
                    v-if="canViewCategoryAnalytics"
                    :class="['analytics-card', 'analytics-card-wide', { dark: theme === THEMES.DARK }]"
                >
                    <div class="analytics-card-header">
                        <div>
                            <h3>Category Revenue</h3>
                            <p>Product category share from paid invoice items</p>
                        </div>
                        <router-link class="mini-link" :to="reportRoute('/reports/sale-by-classification')">Report</router-link>
                    </div>
                    <div class="analytics-chart-frame category-frame">
                        <div v-if="analyticsLoading" class="chart-state" role="status" aria-live="polite">Loading categories…</div>
                        <div v-else-if="analyticsErrors.categories" class="chart-state error">Category revenue could not load.</div>
                        <EChartsHorizontalBarChart
                            v-else-if="categoryChartCategories.length"
                            :categories="categoryChartCategories"
                            :values="categoryChartValues"
                            series-name="Revenue"
                            aria-label="Product category revenue ranking"
                            :is-dark="theme === THEMES.DARK"
                            :value-formatter="formatCurrencyDisplay"
                        />
                        <div v-else class="chart-state">No category revenue data.</div>
                    </div>
                </div>
            </section>

            <section v-if="canViewRevenueAnalytics" :class="['operating-bridge', { dark: theme === THEMES.DARK }]">
                <div class="operating-bridge-header">
                    <div>
                        <p class="dashboard-eyebrow">Gross-to-net</p>
                        <h2>Sales operating bridge</h2>
                        <p>{{ analyticsDateRangeLabel }} · pre-tax · focused scale</p>
                    </div>
                    <span class="bridge-source-label">Sales Report aligned</span>
                </div>
                <div v-if="analyticsLoading" class="chart-state bridge-state" role="status" aria-live="polite">Loading sales operating bridge…</div>
                <div v-else-if="operatingSummaryError" class="chart-state error bridge-state">Sales operating bridge could not load.</div>
                <div v-else-if="!Number(operatingSummary.orders || 0)" class="chart-state bridge-state">
                    No completed sales to bridge for this period and location.
                </div>
                <div v-else class="operating-bridge-layout">
                    <div class="bridge-chart-panel">
                        <div class="bridge-chart-context">
                            <span>Gross sales less discounts and refunds</span>
                            <strong>{{ operatingRetentionPercent }}% retained</strong>
                        </div>
                        <div class="bridge-chart-frame">
                            <EChartsWaterfallChart
                                :gross-sales="Number(operatingSummary.grossSales || 0)"
                                :discounts="Number(operatingSummary.discounts || 0)"
                                :refunds="Number(operatingSummary.refunds || 0)"
                                :net-sales="Number(operatingSummary.netSales || 0)"
                                aria-label="Gross sales to net sales waterfall after discounts and refunds"
                                :is-dark="theme === THEMES.DARK"
                            />
                        </div>
                    </div>
                    <aside class="bridge-diagnostics" aria-label="Sales reconciliation checks">
                        <div class="bridge-net-summary">
                            <p>Net sales</p>
                            <strong>{{ formatCurrencyDisplay(operatingSummary.netSales || 0) }}</strong>
                            <span>{{ operatingDeductionLabel }}</span>
                        </div>
                        <dl>
                            <div v-for="item in operatingDiagnosticRows" :key="item.title">
                                <dt>{{ item.title }}</dt>
                                <dd :class="item.className">{{ item.value }}</dd>
                            </div>
                        </dl>
                    </aside>
                </div>
            </section>

            <!-- Sales Table -->
            <div v-if="canViewRevenueAnalytics && sales.length > 0">
                <div class="mb-3">
                    <h3>Sales List ({{ totalSales }} total)</h3>
                </div>

                <SortableTable 
                    :data="sales"
                    :columns="tableColumns"
                    :defaultSort="sortBy"
                    :defaultSortOrder="sortOrder"
                    :itemKey="'id'"
                    @sort="onTableSort"
                >
                    <template #cell-name="{ item }">
                        <LinkComponent v-if="canViewInvoice" :to="invoiceRoute(item.name)" class="invoice-link">
                            {{ item.name }}
                        </LinkComponent>
                        <span v-else>{{ item.name }}</span>
                    </template>
                    
                    <template #cell-customer="{ item }">
                        <div class="customer-info">
                            <strong>{{ item.customer_name || 'Guest Customer' }}</strong>
                            <br />
                            <small class="text-muted">{{ item.customer_email_id }}</small>
                        </div>
                    </template>
                    
                    <template #cell-set_warehouse="{ item }">
                        {{ item.set_warehouse }}
                    </template>
                    
                    <template #cell-items="{ item }">
                        <span class="badge badge-info">{{ item.total_items || item.total_qty || 0 }} items</span>
                    </template>
                    
                    <template #cell-grand_total="{ item }">
                        <strong class="text-success">${{ formatCurrency(item.grand_total) }}</strong>
                    </template>
                    
                    <template #cell-payment_status="{ item }">
                        <span :class="getPaymentStatusClass(item.payment_status)">
                            {{ item.payment_status || 'Paid' }}
                        </span>
                    </template>
                    
                    <template #cell-createdAt="{ item }">
                        {{ item.formattedCreatedDate || formatDate(item.createdAt) }}
                    </template>
                    
                    <template #cell-actions="{ item }">
                        <div class="action-buttons">
                            <button 
                                class="btn btn-sm btn-outline-primary mr-1" 
                                @click.stop="viewCustomer(item.customer)"
                                v-if="item.customer"
                                title="View Customer"
                                aria-label="View customer"
                            >
                                <i class="material-icons" aria-hidden="true">person</i>
                            </button>
                            <button 
                                v-if="canViewInvoice"
                                class="btn btn-sm btn-outline-secondary mr-1" 
                                @click.stop="viewProducts(item)"
                                title="View Products"
                                :aria-label="`View products for ${item.name}`"
                            >
                                <i class="material-icons" aria-hidden="true">inventory</i>
                            </button>
                            <button 
                                v-if="canViewInvoice"
                                class="btn btn-sm btn-outline-info" 
                                @click.stop="viewInvoice(item.name)"
                                title="View Invoice"
                                :aria-label="`View invoice ${item.name}`"
                            >
                                <i class="material-icons" aria-hidden="true">receipt</i>
                            </button>
                        </div>
                    </template>
                </SortableTable>

                <Paginator @change="updatePage" :page="page" :totalPages="totalPages" />
            </div>

            <div v-else-if="canViewRevenueAnalytics && !loadingSales" class="text-center py-5">
                <i class="material-icons text-muted" style="font-size: 48px" aria-hidden="true">shopping_cart</i>
                <h4 class="text-muted mt-2">No sales found</h4>
                <p class="text-muted">Try adjusting your filters or date range</p>
            </div>

            <!-- Loading State -->
            <div v-if="canViewRevenueAnalytics && loadingSales" class="text-center py-5">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading…</span>
                </div>
                <p class="mt-2" role="status" aria-live="polite">Loading sales data…</p>
            </div>

            <AnalyticsGoalPanel :is-dark="theme === THEMES.DARK" active-goal-id="sales-revenue" />
        </div>

        <!-- Modals -->
        <SaleProductsModal 
            v-if="canViewInvoice && showProductsModal"
            :sale="selectedSale"
            @close="showProductsModal = false"
        />
    </div>
</template>

<script>
import { http } from "@/utils/http"
import Button from "../../components/UI/Button.vue"
import { mapState, mapActions } from "vuex"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import Paginator from "../../components/other/Paginator.vue"
import CustomTable from "../../components/layout/CustomTable.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import LinkComponent from "../../components/UI/LinkComponent.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import SaleProductsModal from "../../components/modals/SaleProductsModal.vue"
import SalesFilters from "../../components/sales/SalesFilters.vue"
import SortableTable from "../../components/table/SortableTable.vue"
import AnalyticsGoalPanel from "../../components/analytics/AnalyticsGoalPanel.vue"
import EChartsDistributionChart from "../../components/charts/EChartsDistributionChart.vue"
import EChartsHorizontalBarChart from "../../components/charts/EChartsHorizontalBarChart.vue"
import EChartsLineChart from "../../components/charts/EChartsLineChart.vue"
import EChartsSalesHeatmap from "../../components/charts/EChartsSalesHeatmap.vue"
import EChartsStoreRankChart from "../../components/charts/EChartsStoreRankChart.vue"
import EChartsWaterfallChart from "../../components/charts/EChartsWaterfallChart.vue"
import SalesFetchApi from "../../utils/fetch/sales-fetch-api"
import {
    formatBusinessChartLabel,
    formatBusinessInstant,
    getBusinessDateRangeForPreset,
    isBusinessDate,
} from "../../utils/business-date"

const SALES_DASHBOARD_PATH = "/dashboard/sales-revenue"
const SALES_DASHBOARD_PERIODS = new Set([
    "today",
    "yesterday",
    "last7days",
    "last30days",
    "last90days",
    "last180days",
    "thisMonth",
    "lastMonth",
    "last3months",
    "last6months",
    "thisYear",
    "lastYear",
    "rollingYear",
    "custom",
])
const SALES_DASHBOARD_SORT_FIELDS = new Set(["name", "set_warehouse", "grand_total", "payment_status", "saleDate"])
const SALES_DASHBOARD_GROUPS = new Set(["day", "week", "month"])

function getDefaultSalesTimeFrame() {
    const range = getBusinessDateRangeForPreset("last7days")

    return {
        startDate: range.start,
        endDate: range.end,
        period: 'last7days'
    }
}

function normalizeLocationFilter(value) {
    return value == null ? "" : String(value)
}

function firstQueryValue(value) {
    return Array.isArray(value) ? value[0] : value
}

function positiveQueryInteger(value, fallback = 1) {
    const parsed = Number.parseInt(firstQueryValue(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

export function getSalesDashboardRouteState(query = {}, isAdmin = false) {
    const defaultTimeFrame = getDefaultSalesTimeFrame()
    const periodValue = String(firstQueryValue(query.period) || "")
    const startDate = String(firstQueryValue(query.date_range_start) || "")
    const endDate = String(firstQueryValue(query.date_range_end) || "")
    const hasValidDateRange = isBusinessDate(startDate) && isBusinessDate(endDate) && startDate <= endDate
    const period = SALES_DASHBOARD_PERIODS.has(periodValue)
        ? periodValue
        : hasValidDateRange
            ? "custom"
            : defaultTimeFrame.period
    const sortByValue = String(firstQueryValue(query.sort_by) || "")
    const sortOrderValue = String(firstQueryValue(query.sort_order) || "")
    const groupByValue = String(firstQueryValue(query.group_by) || "")

    return {
        page: positiveQueryInteger(query.page),
        timeFrame: hasValidDateRange
            ? { startDate, endDate, period }
            : defaultTimeFrame,
        filters: {
            search: String(firstQueryValue(query.search) || ""),
            location: normalizeLocationFilter(firstQueryValue(query.location)),
            includePilotSales:
                Boolean(isAdmin) && String(firstQueryValue(query.includePilotSales) || "") === "true",
        },
        sortBy: SALES_DASHBOARD_SORT_FIELDS.has(sortByValue) ? sortByValue : "saleDate",
        sortOrder: sortOrderValue === "asc" ? "asc" : "desc",
        trendGroupBy: SALES_DASHBOARD_GROUPS.has(groupByValue) ? groupByValue : "day",
    }
}

export default {
    name: "SalesDashboard",
    data() {
        const routeState = getSalesDashboardRouteState(
            this.$route?.query,
            Boolean(this.$store?.state?.auth?.user?.isAdmin),
        )
        return {
            page: routeState.page,
            limit: 15,
            totalSales: 0,
            totalPages: 1,
            loadingSales: false,
            sales: [],
            timeFrame: routeState.timeFrame,
            filters: routeState.filters,
            metrics: {
                totalOrders: 0,
                totalRevenue: 0,
                avgOrderValue: 0,
                uniqueCustomers: 0
            },
            analyticsLoading: false,
            analyticsErrors: {
                revenueTrend: false,
                tenders: false,
                stores: false,
                products: false,
                categories: false,
                basket: false,
                heatmap: false,
                storeRank: false,
            },
            analyticsRequestId: 0,
            analyticsPendingRequestKey: "",
            dashboardLocationFilter: "",
            dashboardIncludePilotSales: null,
            salesRequestId: 0,
            salesSummaryRequestId: 0,
            trendGroupBy: routeState.trendGroupBy,
            revenueTrend: [],
            tenderMix: [],
            tenderSummary: {},
            salesByStore: [],
            topProducts: [],
            categoryRevenue: [],
            operatingSummary: {},
            operatingSummaryError: false,
            basketSummary: {},
            basketDistribution: [],
            heatmapCells: [],
            heatmapSummary: {},
            storeRankPeriods: [],
            storeRankStores: [],
            storeRankSummary: {},
            sortBy: routeState.sortBy,
            sortOrder: routeState.sortOrder,
            showProductsModal: false,
            selectedSale: null,
            tableColumns: [
                { key: 'name', title: 'Invoice #', width: '120px' },
                { key: 'customer', title: 'Customer', width: '150px', sortable: false },
                { key: 'set_warehouse', title: 'Location', width: '100px' },
                { key: 'items', title: 'Items', width: '80px', sortable: false },
                { key: 'grand_total', title: 'Total Amount', width: '100px', type: 'currency' },
                { key: 'payment_status', title: 'Payment Status', width: '100px' },
                { key: 'formattedSaleDate', sortKey: 'saleDate', title: 'Sale Date', width: '150px' },
                { key: 'actions', title: 'Actions', width: '150px', sortable: false }
            ],
            PERMISSION_CODES,
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"]),
        ...mapState("auth", ["user"]),
        analyticsDateRangeLabel() {
            return `${this.timeFrame.startDate} to ${this.timeFrame.endDate}`
        },
        timeFramePeriodLabel() {
            const labels = {
                today: "Today",
                yesterday: "Yesterday",
                last7days: "Last 7 days",
                last30days: "Last 30 days",
                last90days: "Last 90 days",
                last180days: "Last 180 days",
                thisMonth: "This month",
                lastMonth: "Last month",
                last3months: "Last 3 months",
                last6months: "Last 6 months",
                thisYear: "This year",
                lastYear: "Last year",
                rollingYear: "Rolling year",
                custom: "Custom range",
            }
            return labels[this.timeFrame.period] || "Selected period"
        },
        routeIncludePilotSales() {
            return Boolean(this.user?.isAdmin && this.$route?.query?.includePilotSales === "true")
        },
        canViewInvoice() {
            return this.hasReportPermission(PERMISSION_CODES.CMS_VIEW_INVOICE)
        },
        canViewRevenueAnalytics() {
            return this.hasReportPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_REPORT)
        },
        canViewStoreAnalytics() {
            return this.hasReportPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_BY_LOCATION)
        },
        canViewProductAnalytics() {
            return this.hasReportPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_BY_PRODUCT)
        },
        canViewCategoryAnalytics() {
            return this.hasReportPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_BY_CLASSIFICATION)
        },
        canViewAnySalesAnalytics() {
            return this.canViewRevenueAnalytics
                || this.canViewStoreAnalytics
                || this.canViewProductAnalytics
                || this.canViewCategoryAnalytics
        },
        canViewSalesDashboard() {
            return this.canViewAnySalesAnalytics
        },
        revenueTrendLabels() {
            return this.revenueTrend.map(point => formatBusinessChartLabel(point, this.trendGroupBy))
        },
        revenueTrendValues() {
            return this.revenueTrend.map(point => this.roundMoney(point.revenue))
        },
        storeChartRows() {
            return this.salesByStore
                .slice()
                .filter(store => !this.filters.location || String(store.warehouse_id) === String(this.filters.location))
                .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
                .slice(0, 6)
        },
        storeChartCategories() {
            return this.storeChartRows.map(store => store.warehouse_name || "Unknown Store")
        },
        storeChartValues() {
            return this.storeChartRows.map(store => this.roundMoney(store.revenue))
        },
        productChartRows() {
            return this.topProducts
                .slice()
                .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
                .slice(0, 6)
        },
        productChartCategories() {
            return this.productChartRows.map(product => product.product_name || "Unknown Product")
        },
        productChartValues() {
            return this.productChartRows.map(product => this.roundMoney(product.revenue))
        },
        categoryChartRows() {
            return this.categoryRevenue
                .slice()
                .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
                .slice(0, 8)
        },
        categoryChartCategories() {
            return this.categoryChartRows.map(category => category.category || "Uncategorized")
        },
        categoryChartValues() {
            return this.categoryChartRows.map(category => this.roundMoney(category.revenue))
        },
        tenderChartRows() {
            return this.tenderMix
                .slice()
                .sort((a, b) => Number(b.netAmount || 0) - Number(a.netAmount || 0))
                .slice(0, 8)
        },
        tenderChartCategories() {
            return this.tenderChartRows.map(tender => tender.paymentMode || "Unspecified")
        },
        tenderChartValues() {
            return this.tenderChartRows.map(tender => this.roundMoney(tender.netAmount))
        },
        tenderReconciliationLabel() {
            const variance = this.roundMoney(this.tenderSummary.variance || 0)
            return `Net paid after refunds · variance ${this.formatCurrencyDisplay(variance)}`
        },
        operatingRetentionPercent() {
            const gross = Number(this.operatingSummary.grossSales || 0)
            const net = Number(this.operatingSummary.netSales || 0)
            return gross > 0 ? ((net * 100) / gross).toFixed(1) : "0.0"
        },
        operatingDeductionLabel() {
            const deductions = Number(this.operatingSummary.discounts || 0) + Number(this.operatingSummary.refunds || 0)
            return `${this.formatCurrencyDisplay(deductions)} deducted from gross sales`
        },
        operatingDiagnosticRows() {
            return [
                { title: "Tax collected", value: this.formatCurrencyDisplay(this.operatingSummary.tax || 0) },
                { title: "Paid field", value: this.formatCurrencyDisplay(this.operatingSummary.paid || 0) },
                {
                    title: "Grand total − paid",
                    value: this.formatCurrencyDisplay(this.operatingSummary.paidVariance || 0),
                    className: Math.abs(Number(this.operatingSummary.paidVariance || 0)) >= 0.01 ? "has-variance" : "is-reconciled",
                },
                { title: "Units / order", value: Number(this.operatingSummary.unitsPerOrder || 0).toFixed(2) },
            ]
        },
        basketKpiCards() {
            return [
                { title: "Average pre-tax basket", value: this.formatCurrencyDisplay(this.basketSummary.averagePreTaxBasket || 0) },
                { title: "Average units / order", value: Number(this.basketSummary.averageUnitsPerOrder || 0).toFixed(2) },
                { title: "Median units / order", value: Number(this.basketSummary.medianUnitsPerOrder || 0).toFixed(2) },
                { title: "Distinct products / order", value: Number(this.basketSummary.averageDistinctProductsPerOrder || 0).toFixed(2) },
            ]
        },
        basketDistributionCategories() {
            return this.basketDistribution.map(bucket => bucket.label)
        },
        basketDistributionValues() {
            return this.basketDistribution.map(bucket => Number(bucket.orders || 0))
        },
        basketCoverageLabel() {
            const orders = Number(this.basketSummary.orderCount || 0)
            const covered = Math.max(0, orders - Number(this.basketSummary.ordersWithoutItemRows || 0))
            const variance = Number(this.basketSummary.unitVariance || 0).toFixed(2)
            return `${orders} completed orders · item rows for ${covered}/${orders} · unit variance ${variance}`
        },
        heatmapCoverageLabel() {
            const orders = Number(this.heatmapSummary.orderCount || 0)
            const bucketed = Number(this.heatmapSummary.bucketedOrders || 0)
            return `Completed sales · America/Toronto · ${bucketed}/${orders} orders have an hourly bucket`
        },
        storeRankCoverageLabel() {
            const periodCount = Number(this.storeRankSummary.periodCount || 0)
            const storeCount = Number(this.storeRankSummary.storeCount || 0)
            if (this.filters.location) {
                return `${periodCount} ${this.trendGroupBy} periods · selected store ranked among ${storeCount}`
            }
            return `${periodCount} ${this.trendGroupBy} periods · top ${Math.min(storeCount, 5)} stores by total revenue`
        },
        storeRankChartStores() {
            if (!this.filters.location) return this.storeRankStores
            const selected = this.storeRankStores.find(store =>
                String(store.warehouseId || "") === String(this.filters.location)
            )
            return selected ? [selected] : []
        },
    },
    components: {
        SelectDropdown,
        LinkComponent,
        CustomInput,
        CustomTable,
        SortableTable,
        Paginator,
        AnalyticsGoalPanel,
        Button,
        EChartsDistributionChart,
        EChartsHorizontalBarChart,
        EChartsLineChart,
        EChartsSalesHeatmap,
        EChartsStoreRankChart,
        EChartsWaterfallChart,
        SaleProductsModal,
        SalesFilters
    },
    async mounted() {
        this.setData({ pageTitle: "Sales & Revenue" })
        if (this.canViewSalesDashboard) {
            await this.refreshSalesDashboard()
        }
    },
    watch: {
        routeIncludePilotSales(nextValue) {
            this.syncPilotModeFromRoute(nextValue)
        },
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        
        onTimeFrameChange(newTimeFrame) {
            this.timeFrame = newTimeFrame
            this.page = 1
            this.refreshSalesDashboard()
        },
        
        onFiltersChange(newFilters) {
            const nextLocation = normalizeLocationFilter(newFilters.location)
            const locationChanged = this.dashboardLocationFilter !== nextLocation
            const historicalModeChanged =
                Boolean(this.dashboardIncludePilotSales) !== Boolean(newFilters.includePilotSales)
            this.filters = { ...newFilters }
            this.page = 1

            if (historicalModeChanged) {
                this.resetVisibleSalesScope()
            }

            if (locationChanged || historicalModeChanged) {
                this.refreshSalesDashboard()
                return
            }

            if (this.canViewRevenueAnalytics) {
                this.fetchSales()
            }
        },

        syncPilotModeFromRoute(nextValue) {
            const includePilotSales = Boolean(nextValue)
            if (Boolean(this.filters.includePilotSales) === includePilotSales) return

            this.filters = { ...this.filters, includePilotSales }
            this.page = 1
            this.resetVisibleSalesScope()
            this.refreshSalesDashboard()
        },

        resetVisibleSalesScope() {
            this.analyticsRequestId = (Number(this.analyticsRequestId) || 0) + 1
            this.analyticsPendingRequestKey = ""
            this.analyticsLoading = false
            this.revenueTrend = []
            this.tenderMix = []
            this.tenderSummary = {}
            this.salesByStore = []
            this.topProducts = []
            this.categoryRevenue = []
            this.operatingSummary = {}
            this.operatingSummaryError = false
            this.basketSummary = {}
            this.basketDistribution = []
            this.heatmapCells = []
            this.heatmapSummary = {}
            this.storeRankPeriods = []
            this.storeRankStores = []
            this.storeRankSummary = {}
            this.resetSalesReportData()
        },
        
        onTableSort(sortInfo) {
            this.sortBy = sortInfo.column
            this.sortOrder = sortInfo.order
            this.page = 1 // Reset to first page when sorting
            this.fetchSales()
        },

        async refreshSalesDashboard() {
            this.dashboardLocationFilter = normalizeLocationFilter(this.filters.location)
            this.dashboardIncludePilotSales = Boolean(this.filters.includePilotSales)
            const requests = [this.fetchAnalyticsDashboardData()]

            if (this.canViewRevenueAnalytics) {
                requests.push(this.fetchSales(), this.fetchSalesSummary())
            } else {
                this.resetSalesReportData()
            }

            await Promise.all(requests)
        },

        async fetchAnalyticsDashboardData() {
            if (!this.canViewAnySalesAnalytics) {
                this.revenueTrend = []
                this.tenderMix = []
                this.tenderSummary = {}
                this.salesByStore = []
                this.topProducts = []
                this.categoryRevenue = []
                this.operatingSummary = {}
                this.operatingSummaryError = false
                this.basketSummary = {}
                this.basketDistribution = []
                this.heatmapCells = []
                this.heatmapSummary = {}
                this.storeRankPeriods = []
                this.storeRankStores = []
                this.storeRankSummary = {}
                this.analyticsErrors = {
                    revenueTrend: false,
                    tenders: false,
                    stores: false,
                    products: false,
                    categories: false,
                    basket: false,
                    heatmap: false,
                    storeRank: false,
                }
                return
            }

            const requestKey = [
                this.timeFrame.startDate,
                this.timeFrame.endDate,
                this.filters.location || "",
                this.filters.includePilotSales ? "pilot" : "production",
                this.trendGroupBy,
                [
                    this.canViewRevenueAnalytics,
                    this.canViewStoreAnalytics,
                    this.canViewProductAnalytics,
                    this.canViewCategoryAnalytics,
                ].join(","),
            ].join("|")

            if (requestKey === this.analyticsPendingRequestKey) return

            const requestId = ++this.analyticsRequestId
            this.analyticsPendingRequestKey = requestKey
            this.analyticsLoading = true
            this.analyticsErrors = {
                revenueTrend: false,
                tenders: false,
                stores: false,
                products: false,
                categories: false,
                basket: false,
                heatmap: false,
                storeRank: false,
            }

            const baseOptions = {
                startDate: this.timeFrame.startDate,
                endDate: this.timeFrame.endDate,
                includePilotSales: this.filters.includePilotSales,
            }
            const warehouseOptions = this.filters.location
                ? { ...baseOptions, warehouse_id: this.filters.location }
                : baseOptions

            try {
                const [
                    revenueTrendResponse,
                    tenderResponse,
                    storeResponse,
                    productResponse,
                    categoryResponse,
                    operatingResponse,
                    basketResponse,
                    heatmapResponse,
                    storeRankResponse,
                ] = await Promise.allSettled([
                    this.canViewRevenueAnalytics
                        ? SalesFetchApi.getSalesOverTime({
                            ...baseOptions,
                            groupBy: this.trendGroupBy,
                            warehouse_ids: this.filters.location,
                        })
                        : Promise.resolve({ result: [] }),
                    this.canViewRevenueAnalytics
                        ? http.get("/analytics/sales-tender-mix", { params: warehouseOptions })
                        : Promise.resolve({ data: { result: { tenders: [], summary: {} } } }),
                    this.canViewStoreAnalytics
                        ? SalesFetchApi.getSalesByWarehouse(baseOptions)
                        : Promise.resolve({ result: [] }),
                    this.canViewProductAnalytics
                        ? SalesFetchApi.getSalesByProduct({
                            ...warehouseOptions,
                            limit: 20,
                        })
                        : Promise.resolve({ result: [] }),
                    this.canViewCategoryAnalytics
                        ? SalesFetchApi.getProductCategoryBreakdown(warehouseOptions)
                        : Promise.resolve({ categories: [] }),
                    this.canViewRevenueAnalytics
                        ? http.get("/analytics/sales-operating-summary", { params: warehouseOptions })
                        : Promise.resolve({ data: { result: {} } }),
                    this.canViewRevenueAnalytics
                        ? http.get("/analytics/sales-basket-analysis", { params: warehouseOptions })
                        : Promise.resolve({ data: { result: { summary: {}, distribution: [] } } }),
                    this.canViewRevenueAnalytics
                        ? http.get("/analytics/sales-hourly-heatmap", { params: warehouseOptions })
                        : Promise.resolve({ data: { result: { summary: {}, cells: [] } } }),
                    this.canViewStoreAnalytics
                        ? http.get("/analytics/sales-store-rank-trend", {
                            params: { ...baseOptions, interval: this.trendGroupBy },
                        })
                        : Promise.resolve({ data: { result: { periods: [], stores: [], summary: {} } } }),
                ])

                if (requestId !== this.analyticsRequestId) return

                if (revenueTrendResponse.status === "fulfilled") {
                    this.revenueTrend = revenueTrendResponse.value?.result || []
                } else {
                    this.analyticsErrors.revenueTrend = true
                    this.revenueTrend = []
                }

                if (tenderResponse.status === "fulfilled") {
                    this.tenderMix = tenderResponse.value?.data?.result?.tenders || []
                    this.tenderSummary = tenderResponse.value?.data?.result?.summary || {}
                } else {
                    this.analyticsErrors.tenders = true
                    this.tenderMix = []
                    this.tenderSummary = {}
                }

                if (storeResponse.status === "fulfilled") {
                    this.salesByStore = storeResponse.value?.result || []
                } else {
                    this.analyticsErrors.stores = true
                    this.salesByStore = []
                }

                if (productResponse.status === "fulfilled") {
                    this.topProducts = productResponse.value?.result || []
                } else {
                    this.analyticsErrors.products = true
                    this.topProducts = []
                }

                this.operatingSummary = operatingResponse.status === "fulfilled"
                    ? operatingResponse.value?.data?.result || {}
                    : {}
                this.operatingSummaryError = operatingResponse.status !== "fulfilled"

                if (basketResponse.status === "fulfilled") {
                    this.basketSummary = basketResponse.value?.data?.result?.summary || {}
                    this.basketDistribution = basketResponse.value?.data?.result?.distribution || []
                } else {
                    this.analyticsErrors.basket = true
                    this.basketSummary = {}
                    this.basketDistribution = []
                }

                if (heatmapResponse.status === "fulfilled") {
                    this.heatmapCells = heatmapResponse.value?.data?.result?.cells || []
                    this.heatmapSummary = heatmapResponse.value?.data?.result?.summary || {}
                } else {
                    this.analyticsErrors.heatmap = true
                    this.heatmapCells = []
                    this.heatmapSummary = {}
                }

                if (storeRankResponse.status === "fulfilled") {
                    this.storeRankPeriods = storeRankResponse.value?.data?.result?.periods || []
                    this.storeRankStores = storeRankResponse.value?.data?.result?.stores || []
                    this.storeRankSummary = storeRankResponse.value?.data?.result?.summary || {}
                } else {
                    this.analyticsErrors.storeRank = true
                    this.storeRankPeriods = []
                    this.storeRankStores = []
                    this.storeRankSummary = {}
                }

                if (categoryResponse.status === "fulfilled") {
                    this.categoryRevenue = categoryResponse.value?.categories || []
                } else {
                    this.analyticsErrors.categories = true
                    this.categoryRevenue = []
                }
            } catch (err) {
                if (requestId !== this.analyticsRequestId) return
                console.log("error in fetching sales analytics", err)
                this.analyticsErrors = {
                    revenueTrend: true,
                    tenders: true,
                    stores: true,
                    products: true,
                    categories: true,
                    basket: true,
                    heatmap: true,
                    storeRank: true,
                }
                this.revenueTrend = []
                this.tenderMix = []
                this.tenderSummary = {}
                this.salesByStore = []
                this.topProducts = []
                this.categoryRevenue = []
                this.operatingSummary = {}
                this.operatingSummaryError = true
                this.basketSummary = {}
                this.basketDistribution = []
                this.heatmapCells = []
                this.heatmapSummary = {}
                this.storeRankPeriods = []
                this.storeRankStores = []
                this.storeRankSummary = {}
            } finally {
                if (requestId === this.analyticsRequestId) {
                    this.analyticsLoading = false
                    this.analyticsPendingRequestKey = ""
                }
            }
        },

        buildSalesReportParams({ includeSearch = true, limit = this.limit, page = this.page, includeSort = true } = {}) {
            const params = new URLSearchParams({
                limit: limit.toString(),
                page: page.toString(),
                date_range_start: this.timeFrame.startDate,
                date_range_end: this.timeFrame.endDate
            })

            if (includeSearch && this.filters.search) {
                params.append('search', this.filters.search)
            }

            if (this.filters.location) {
                params.append('warehouse_ids', this.filters.location)
            }

            if (this.filters.includePilotSales) {
                params.append('includePilotSales', 'true')
            }

            if (includeSort && this.sortBy) {
                params.append('sort_by', this.sortBy)
                params.append('sort_order', this.sortOrder)
            }

            return params
        },

        async fetchSales() {
            const requestId = (Number(this.salesRequestId) || 0) + 1
            this.salesRequestId = requestId
            if (!this.canViewRevenueAnalytics) {
                this.resetSalesReportData()
                return
            }

            try {
                this.loadingSales = true

                const params = this.buildSalesReportParams()

                const res = await http.get(`/reports/sales-general-report?${params.toString()}`)

                if (requestId !== this.salesRequestId) return

                // Use backend response directly (no frontend filtering needed)
                this.sales = res.data.result || []
                this.totalSales = res.data.totalResults || 0
                this.totalPages = res.data.totalPages || 1
                
            } catch (err) {
                if (requestId !== this.salesRequestId) return
                console.log("error in fetching sales", err)
                this.$toast.error("Error fetching sales data")
            } finally {
                if (requestId === this.salesRequestId) {
                    this.loadingSales = false
                }
            }
        },

        async fetchSalesSummary() {
            const requestId = (Number(this.salesSummaryRequestId) || 0) + 1
            this.salesSummaryRequestId = requestId
            if (!this.canViewRevenueAnalytics) {
                this.resetSalesReportData()
                return
            }

            try {
                const params = this.buildSalesReportParams({
                    includeSearch: false,
                    includeSort: false,
                    limit: 1,
                    page: 1,
                })
                const res = await http.get(`/reports/sales-general-report?${params.toString()}`)

                if (requestId !== this.salesSummaryRequestId) return

                if (res.data.metrics) {
                    this.metrics = res.data.metrics
                }
            } catch (err) {
                if (requestId !== this.salesSummaryRequestId) return
                console.log("error in fetching sales summary", err)
                this.$toast.error("Error fetching sales summary")
            }
        },

        resetSalesReportData() {
            this.salesRequestId = (Number(this.salesRequestId) || 0) + 1
            this.salesSummaryRequestId = (Number(this.salesSummaryRequestId) || 0) + 1
            this.loadingSales = false
            this.sales = []
            this.totalSales = 0
            this.totalPages = 1
            this.metrics = {
                totalOrders: 0,
                totalRevenue: 0,
                avgOrderValue: 0,
                uniqueCustomers: 0
            }
        },



        updatePage(newPage) {
            if (this.totalPages > 0) {
                if (newPage > 0 && newPage <= this.totalPages) {
                    this.page = newPage
                    this.fetchSales()
                } else {
                    this.$toast.error("Invalid page!")
                }
            }
        },

        resetFilters() {
            this.filters = {
                search: "",
                location: "",
                includePilotSales: false
            }
            this.timeFrame = getDefaultSalesTimeFrame()
            this.sortBy = 'saleDate'
            this.sortOrder = 'desc'
            this.page = 1
            this.refreshSalesDashboard()
        },

        roundMoney(value) {
            return Math.round((Number(value) || 0) * 100) / 100
        },

        formatCurrencyDisplay(value) {
            return `$${this.roundMoney(value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`
        },

        formatCurrency(amount) {
            if (!amount) return "0.00"
            return parseFloat(amount).toFixed(2)
        },

        reportRoute(path) {
            const query = {
                date_range_start: this.timeFrame.startDate,
                date_range_end: this.timeFrame.endDate,
            }
            if (this.filters.location) query.warehouse_ids = this.filters.location
            if (this.filters.includePilotSales) query.includePilotSales = "true"
            return { path, query }
        },
        formatTrendLabel(point) {
            const chartPoint = typeof point === "string" ? { timestamp: point } : point
            return formatBusinessChartLabel(chartPoint, this.trendGroupBy)
        },

        hasReportPermission(permissionId) {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },

        getPaymentStatusClass(status) {
            const classes = {
                'Paid': 'badge badge-success',
                'Pending': 'badge badge-warning', 
                'Failed': 'badge badge-danger',
                'Refunded': 'badge badge-secondary'
            }
            return classes[status] || 'badge badge-success'
        },

        viewSaleDetails(sale) {
            // Navigate to invoice detail page
            this.$router.push(this.invoiceRoute(sale.name))
        },

        viewCustomer(customerId) {
            if (customerId) {
                this.$router.push(`/customer-edit/${customerId}`)
            }
        },

        viewProducts(sale) {
            this.selectedSale = sale
            this.showProductsModal = true
        },

        viewInvoice(invoiceName) {
            this.$router.push(this.invoiceRoute(invoiceName))
        },

        invoiceRoute(invoiceName) {
            return {
                path: `/invoices/${invoiceName}`,
                query: {
                    returnTo: this.salesDashboardReturnPath(),
                },
            }
        },

        salesDashboardReturnPath() {
            const query = new URLSearchParams({
                period: this.timeFrame.period,
                date_range_start: this.timeFrame.startDate,
                date_range_end: this.timeFrame.endDate,
            })

            if (this.filters.search) query.set("search", this.filters.search)
            if (this.filters.location) query.set("location", this.filters.location)
            if (this.filters.includePilotSales) query.set("includePilotSales", "true")
            if (this.page > 1) query.set("page", String(this.page))
            if (this.sortBy !== "saleDate") query.set("sort_by", this.sortBy)
            if (this.sortOrder !== "desc") query.set("sort_order", this.sortOrder)
            if (this.trendGroupBy !== "day") query.set("group_by", this.trendGroupBy)

            return `${this.$route?.path || SALES_DASHBOARD_PATH}?${query.toString()}`
        },

        
        formatDate(dateString) {
            if (!dateString) return ''
            return formatBusinessInstant(dateString, { dateOnly: true })
        },
        
        formatTime(dateString) {
            if (!dateString) return ''
            return formatBusinessInstant(dateString)
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.sales-dashboard { padding: $cardPaddingY; }

.dashboard-page-header {
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    margin: 0 0 16px;

    h1 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }
}

.dashboard-eyebrow {
    color: rgba($textColor, 0.56);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    margin: 0 0 4px;
    text-transform: uppercase;
}

.report-link,
.mini-link {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 13px;
    font-weight: 700;
    gap: 6px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        color: $secondaryColor;
        text-decoration: none;
    }

    i {
        font-size: 18px;
    }
}

.sales-analytics-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 18px 0;
}

.analytics-card {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
    min-width: 0;
    padding: 16px;

    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.analytics-card-wide {
    grid-column: 1 / -1;
}

.analytics-card-header {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    justify-content: space-between;
    margin-bottom: 10px;
    min-height: 38px;

    h3 {
        color: $primaryColor;
        font-size: 16px;
        font-weight: 800;
        line-height: 1.25;
        margin: 0;
    }

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        line-height: 1.35;
        margin: 4px 0 0;
    }
}

.dark .analytics-card-header {
    h3 {
        color: $secondaryColorDark;
    }

    p {
        color: rgba($darkTextColor, 0.7);
    }
}

.chart-select {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    color: $textColor;
    font-size: 12px;
    height: 34px;
    min-width: 92px;
    padding: 0 10px;

    &:focus-visible {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba($primaryColor, 0.12);
        outline: none;
    }
}

.dark .chart-select {
    background: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;
}

.analytics-chart-frame {
    min-height: 250px;
    position: relative;

    &.large {
        min-height: 360px;
    }

    &.category-frame {
        min-height: 300px;
    }

    &.basket-frame {
        min-height: 270px;
    }

    &.heatmap-frame,
    &.rank-frame {
        min-height: 340px;
    }
}

.basket-state {
    min-height: 300px;
}

.basket-kpi-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-bottom: 12px;

    article {
        background: rgba($primaryColor, 0.035);
        border: 1px solid $lightBorderColor;
        border-radius: 8px;
        padding: 12px;
    }

    p {
        color: rgba($textColor, 0.62);
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        margin: 0 0 5px;
    }

    strong {
        color: $primaryColor;
        font-size: 19px;
        line-height: 1.2;
    }
}

.dark .basket-kpi-grid {
    article {
        background: rgba($darkTextColor, 0.04);
        border-color: $darkBorderColor;
    }

    p {
        color: rgba($darkTextColor, 0.68);
    }

    strong {
        color: $secondaryColorDark;
    }
}

.chart-state {
    align-items: center;
    background: rgba($textColor, 0.03);
    border: 1px dashed rgba($textColor, 0.16);
    border-radius: 8px;
    color: rgba($textColor, 0.62);
    display: flex;
    font-size: 13px;
    font-weight: 600;
    height: 100%;
    justify-content: center;
    min-height: 180px;
    padding: 20px;
    text-align: center;

    &.error {
        background: rgba($dangerColor, 0.08);
        border-color: rgba($dangerColor, 0.24);
        color: $dangerColor;
    }
}

.dark .chart-state {
    background: rgba($darkTextColor, 0.04);
    border-color: rgba($darkTextColor, 0.14);
    color: rgba($darkTextColor, 0.68);

    &.error {
        background: rgba($dangerColorDark, 0.12);
        border-color: rgba($dangerColorDark, 0.3);
        color: $dangerColorDark;
    }
}

.operating-bridge {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
    box-shadow: $cardShadow;
    margin: 0 0 22px;
    overflow: hidden;
    padding: 18px;

    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.operating-bridge-header {
    align-items: flex-start;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    margin-bottom: 16px;

    h2 {
        color: $primaryColor;
        font-size: 19px;
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1.2;
        margin: 0;
    }

    > div > p:last-child {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        line-height: 1.4;
        margin: 5px 0 0;
    }
}

.bridge-source-label {
    background: rgba($primaryColor, 0.06);
    border: 1px solid rgba($primaryColor, 0.12);
    border-radius: 5px;
    color: rgba($primaryColor, 0.76);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    padding: 7px 9px;
    text-transform: uppercase;
    white-space: nowrap;
}

.operating-bridge-layout {
    display: grid;
    gap: 14px;
    grid-template-columns: minmax(0, 2fr) minmax(250px, 0.8fr);
}

.bridge-chart-panel,
.bridge-diagnostics {
    background: rgba($primaryColor, 0.022);
    border: 1px solid rgba($primaryColor, 0.09);
    border-radius: 8px;
}

.bridge-chart-panel {
    min-width: 0;
    padding: 14px 14px 8px;
}

.bridge-chart-context {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 0 2px 6px;

    span {
        color: rgba($textColor, 0.6);
        font-size: 11px;
        font-weight: 600;
    }

    strong {
        color: $primaryColor;
        font-size: 12px;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
}

.bridge-chart-frame {
    height: 315px;
}

.bridge-diagnostics {
    display: flex;
    flex-direction: column;
    padding: 12px;
}

.bridge-net-summary {
    background: rgba($primaryColor, 0.075);
    border-radius: 7px;
    margin-bottom: 10px;
    padding: 16px;

    p {
        color: rgba($textColor, 0.64);
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.03em;
        margin: 0 0 6px;
        text-transform: uppercase;
    }

    strong {
        color: $primaryColor;
        display: block;
        font-size: 28px;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.025em;
        line-height: 1.1;
        margin-bottom: 7px;
    }

    span {
        color: rgba($textColor, 0.62);
        font-size: 11px;
        line-height: 1.4;
    }
}

.bridge-diagnostics dl {
    margin: 0;

    > div {
        align-items: center;
        border-bottom: 1px solid rgba($primaryColor, 0.08);
        display: flex;
        gap: 14px;
        justify-content: space-between;
        min-height: 46px;
        padding: 8px 4px;

        &:last-child {
            border-bottom: 0;
        }
    }

    dt {
        color: rgba($textColor, 0.62);
        font-size: 11px;
        font-weight: 650;
    }

    dd {
        color: $primaryColor;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        font-weight: 800;
        margin: 0;
        text-align: right;
    }

    .is-reconciled {
        color: $successColor;
    }

    .has-variance {
        color: $warningColor;
    }
}

.bridge-state {
    min-height: 370px;
}

.dark {
    .operating-bridge-header h2,
    .bridge-chart-context strong,
    .bridge-net-summary strong,
    .bridge-diagnostics dd {
        color: $secondaryColorDark;
    }

    .operating-bridge-header > div > p:last-child,
    .bridge-chart-context span,
    .bridge-net-summary p,
    .bridge-net-summary span,
    .bridge-diagnostics dt {
        color: rgba($darkTextColor, 0.68);
    }

    .bridge-source-label,
    .bridge-chart-panel,
    .bridge-diagnostics,
    .bridge-net-summary {
        background: rgba($darkTextColor, 0.045);
        border-color: rgba($darkTextColor, 0.11);
    }

    .bridge-diagnostics dl > div {
        border-color: rgba($darkTextColor, 0.09);
    }
}

.source-requirements {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
    margin: 0 0 18px;
    padding: 16px;

    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.source-requirements-header {
    margin-bottom: 12px;

    h2 {
        color: $primaryColor;
        font-size: 16px;
        font-weight: 800;
        margin: 0;
    }
}

.dark .source-requirements-header h2 {
    color: $secondaryColorDark;
}

.requirement-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));

    article {
        border: 1px solid $lightBorderColor;
        border-radius: 8px;
        padding: 12px;
    }

    h3 {
        color: $primaryColor;
        font-size: 13px;
        font-weight: 800;
        line-height: 1.25;
        margin: 0 0 6px;
    }

    p {
        color: rgba($textColor, 0.64);
        font-size: 12px;
        line-height: 1.45;
        margin: 0;
    }
}

.dark .requirement-grid {
    article {
        border-color: $darkBorderColor;
    }

    h3 {
        color: $secondaryColorDark;
    }

    p {
        color: rgba($darkTextColor, 0.72);
    }
}

.sales-metrics {
    .metric-card {
        background: $lightCardBg;
        border: 1px solid $lightBorderColor;
        border-radius: $cardRadius;
        box-shadow: $cardShadow;
        display: flex;
        align-items: center;
        
        &.dark {
            background: $darkCardBg;
            border-color: $darkBorderColor;
            color: $darkTextColor;
        }
        
        .metric-icon {
            margin-right: 15px;
            
            i {
                font-size: 32px;
            }
        }
        
        .metric-content {
            flex: 1;
            
            .metric-value {
                font-size: 24px;
                font-weight: bold;
                margin: 0;
                line-height: 1.2;
            }
            
            .metric-label {
                margin: 0;
                font-size: 14px;
                color: rgba($textColor, 0.7);
            }
            
            .metric-period {
                font-size: 12px;
                color: rgba($textColor, 0.55);
            }
        }
    }
}

.filter-card {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    
    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.clickable-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: $lightHoverColor;
    }
}
.dark .clickable-row:hover { background-color: lighten($darkCardBg, 4%); }

.customer-info {
    strong {
        color: $primaryColor;
    }
}

.action-buttons {
    display: flex;
    gap: 5px;
    
    .btn {
        padding: 4px 8px;
        
        i {
            font-size: 16px;
        }
    }
}

.invoice-link {
    font-weight: bold;
    color: $primaryColor;
    text-decoration: none;
    
    &:hover {
        color: $secondaryColor;
        text-decoration: underline;
    }
}



.text-success { color: $successColor !important; }
.text-primary { color: $primaryColor !important; }
.text-warning { color: $warningColor !important; }
.text-info { color: $infoColor !important; }
.dark .text-success { color: $successColorDark !important; }
.dark .text-primary { color: $primaryColorDark !important; }
.dark .text-warning { color: $warningColorDark !important; }
.dark .text-info { color: $infoColorDark !important; }

@media (max-width: 768px) {
    .dashboard-page-header,
    .analytics-card-header {
        align-items: stretch;
        flex-direction: column;
    }

    .sales-analytics-grid {
        grid-template-columns: 1fr;
    }

    .analytics-chart-frame,
    .analytics-chart-frame.large,
    .analytics-chart-frame.category-frame {
        min-height: 300px;
    }

    .basket-kpi-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .operating-bridge-header,
    .bridge-chart-context {
        align-items: flex-start;
        flex-direction: column;
    }

    .operating-bridge-layout {
        grid-template-columns: 1fr;
    }

    .bridge-chart-frame {
        height: 330px;
    }

    .sales-metrics .col-md-3 {
        margin-bottom: 15px;
    }
    
    .action-buttons {
        flex-direction: column;
    }
}
</style> 
